import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard, FileText, Upload, Eye, IdCard, Bell, User, Building2,
  Users, Settings, ScrollText, LogOut, ChevronLeft, ChevronRight, Menu, Globe, CalendarDays, Clock, TrendingUp, Shield
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const getNavItems = (t) => ({
  citizen: [
    { label: t('citizen.dashboard'), icon: LayoutDashboard, path: '/citizen/dashboard' },
    { label: t('citizen.new_app'), icon: FileText, path: '/citizen/new-application' },
    { label: t('citizen.documents'), icon: Upload, path: '/citizen/documents' },
    { label: t('citizen.track'), icon: Eye, path: '/citizen/track-status' },
    { label: t('citizen.card'), icon: IdCard, path: '/citizen/my-card' },
    { label: t('appt.title'), icon: CalendarDays, path: '/citizen/appointments' },
    { label: t('citizen.notifications'), icon: Bell, path: '/citizen/notifications' },
    { label: t('citizen.profile'), icon: User, path: '/citizen/profile' },
  ],
  embassy: [
    { label: t('embassy.dashboard'), icon: LayoutDashboard, path: '/embassy/dashboard' },
    { label: t('embassy.applications'), icon: FileText, path: '/embassy/applications' },
    { label: t('schedule.title'), icon: Clock, path: '/embassy/schedule' },
    { label: t('appt.title'), icon: CalendarDays, path: '/embassy/appointments' },
  ],
  admin: [
    { label: t('admin.dashboard'), icon: LayoutDashboard, path: '/admin/dashboard' },
    { label: t('admin.analytics'), icon: TrendingUp, path: '/admin/analytics' },
    { label: t('admin.embassies'), icon: Building2, path: '/admin/embassies' },
    { label: t('admin.users'), icon: Users, path: '/admin/users' },
    { label: t('admin.security'), icon: Shield, path: '/admin/security' },
    { label: t('admin.cms'), icon: Settings, path: '/admin/cms' },
    { label: t('admin.audit'), icon: ScrollText, path: '/admin/audit-logs' },
  ],
});

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { t, lang, switchLang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const sseRef = useRef(null);
  const lastNotifRef = useRef(null);

  const role = user?.role || 'citizen';
  const allNavItems = getNavItems(t);
  const items = allNavItems[role] || allNavItems.citizen;

  // Initial fetch
  useEffect(() => {
    api.get('/notifications/unread-count').then(r => setUnread(r.data.count)).catch(() => {});
  }, [location.pathname]);

  // SSE push notifications
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('jwt_token');
    if (!token) return;

    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const url = `${BACKEND}/api/notifications/stream?token=${encodeURIComponent(token)}`;

    let retryTimeout = null;

    const connect = () => {
      const eventSource = new EventSource(url);

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setUnread(data.unread_count || 0);
          if (data.latest && data.latest.notification_id !== lastNotifRef.current) {
            lastNotifRef.current = data.latest.notification_id;
            const typeMap = { success: 'success', error: 'error', warning: 'warning' };
            const toastFn = typeMap[data.latest.type] ? toast[typeMap[data.latest.type]] : toast.info;
            toastFn(data.latest.title, { description: data.latest.message, duration: 5000 });
          }
        } catch {}
      };

      eventSource.onerror = () => {
        eventSource.close();
        retryTimeout = setTimeout(connect, 15000);
      };

      sseRef.current = eventSource;
    };

    connect();

    return () => {
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col border-r border-border bg-white transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'}`}>
        <div className="p-4 border-b border-border flex items-center justify-between">
          {!collapsed && (
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#007FFF] flex items-center justify-center shrink-0">
                <IdCard className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-sm truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>Diaspora RDC</span>
            </Link>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="p-1 hover:bg-muted rounded" data-testid="sidebar-toggle">
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        <nav className="flex-1 py-4 space-y-1 px-2">
          {items.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.split('/').pop()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                  ${isActive ? 'bg-[#007FFF] text-white' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
                {!collapsed && item.path === '/citizen/notifications' && unread > 0 && (
                  <Badge variant="destructive" className="ml-auto text-xs h-5 min-w-5 flex items-center justify-center">{unread}</Badge>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-border">
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive w-full transition-colors"
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {!collapsed && <span>{t('nav.logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white flex flex-col animate-slide-in">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#007FFF] flex items-center justify-center">
                  <IdCard className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Diaspora RDC</span>
              </Link>
            </div>
            <nav className="flex-1 py-4 space-y-1 px-2">
              {items.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                      ${isActive ? 'bg-[#007FFF] text-white' : 'text-muted-foreground hover:bg-muted'}`}>
                    <Icon className="w-5 h-5 shrink-0" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="p-3 border-t border-border">
              <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive w-full">
                <LogOut className="w-5 h-5" /><span>{t('nav.logout')}</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-white flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 hover:bg-muted rounded" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <p className="text-sm font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('dash.welcome')} {user?.name || 'Utilisateur'}
              </p>
              <p className="text-xs text-muted-foreground">{t(`role.${role}`)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Language toggle */}
            <button
              onClick={() => switchLang(lang === 'fr' ? 'en' : 'fr')}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors"
              data-testid="dash-lang-toggle"
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === 'fr' ? 'EN' : 'FR'}
            </button>
            {role === 'citizen' && (
              <Link to="/citizen/notifications" className="relative p-2 hover:bg-muted rounded-lg" data-testid="header-notif-btn">
                <Bell className="w-5 h-5 text-muted-foreground" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-destructive rounded-full flex items-center justify-center text-[10px] font-bold text-white px-1" data-testid="notif-badge">
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </Link>
            )}
            {role !== 'citizen' && unread > 0 && (
              <div className="relative p-2" data-testid="header-notif-indicator">
                <Bell className="w-5 h-5 text-muted-foreground" />
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-destructive rounded-full flex items-center justify-center text-[10px] font-bold text-white px-1">
                  {unread > 99 ? '99+' : unread}
                </span>
              </div>
            )}
            <div className="w-8 h-8 rounded-full bg-[#007FFF] flex items-center justify-center text-white text-sm font-semibold">
              {(user?.name || 'U').charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
